import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'

import { useSnackbar } from 'components/Snackbar'

import { accountLinkConfirm, getOtpCode, loginUser } from 'store/auth/actions'
import { useDispatch, useSelector } from 'store/hooks'
import { storage } from 'utils'
import { CodeExpiryNotice } from 'components/auth'
import { OTP_CODE_TTL_SECONDS } from 'hooks/useCodeExpiry'

import { Stack, Typography, TextField, Box, CircularProgress } from '@mui/material'

interface OtpVerifyProps {
  handleClose: () => void
  inDrawer?: boolean
  email: string
  linkAccounts?: boolean
}

export const OtpVerify = ({ inDrawer = false, handleClose, email, linkAccounts = false }: OtpVerifyProps) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { openSnackbar } = useSnackbar()

  const [loading, setLoading] = useState<boolean>(false)
  const [otpCodes, setOtpCodes] = useState(['', '', '', ''])
  const [incorrectCode, setIncorrectCode] = useState<boolean>(false)
  /*
   * VRG-720 — send time of the outstanding code. Seeded at mount because the
   * caller (`LinkAccounts`) dispatches `getOtpCode` immediately before opening
   * this screen, and reset in `receiveOtpCode` below.
   */
  const [codeIssuedAt, setCodeIssuedAt] = useState<number | null>(() => Date.now())

  const { otpReference } = useSelector(({ auth }) => auth)
  // ONE ref holding an array of inputs — not `Array.from(..., () => useRef())`.
  // That called `useRef` once per code inside a callback, so the hook COUNT tracked
  // `otpCodes.length`. `handlePaste` accepts /^\d{1,4}$/ and does `pastedText.split('')`,
  // so pasting 1-3 digits shrank the array and React crashed on the next render with a
  // changed hook order. Caught by react-hooks/rules-of-hooks.
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  useEffect(() => {
    const isAllFieldsFilled = otpCodes.every(code => code.length === 1)
    isAllFieldsFilled && onLoginSubmit()
  }, [otpCodes])

  const handleCodeChange = (index: number, value: string) => {
    const newOtpCodes = [...otpCodes]
    if (value.length <= 1) {
      newOtpCodes[index] = value
      setOtpCodes(newOtpCodes)

      if (value.length === 1 && index < newOtpCodes.length - 1) {
        inputRefs.current[index + 1]?.focus()
      }
    }
  }

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedText = event.clipboardData.getData('text')

    if (/^\d{1,4}$/.test(pastedText)) {
      const newOtpCodes = pastedText.split('')
      setOtpCodes(newOtpCodes)
    }
    event.preventDefault()
  }

  const receiveOtpCode = () => {
    dispatch(
      getOtpCode({ email: email }, ({ success }) => {
        if (success) setCodeIssuedAt(Date.now())
        success &&
          openSnackbar({
            severity: 'info',
            title: `The code was sent to ${email}, please enter the code`,
          })
      })
    )
  }

  const onLoginSubmit = () => {
    const otpCode = otpCodes.join('')
    const formData = { otpCode: otpCode, otpReference: otpReference }
    setLoading(true)

    linkAccounts
      ? dispatch(
          accountLinkConfirm(formData, ({ success }) => {
            setLoading(false)

            if (success) {
              inDrawer ? handleClose() : navigate('/')
              openSnackbar({
                severity: 'success',
                title: `Your accounts have been linked`,
              })
            } else {
              setIncorrectCode(true)
            }
          })
        )
      : dispatch(
          loginUser(formData, ({ success }) => {
            setLoading(false)

            if (success) {
              const token = storage.getItem('accessToken')
              const redirectTo = storage.getItem('redirectTo')
              const apiUrl = import.meta.env.REACT_APP_API_URL

              inDrawer && handleClose()
              redirectTo &&
                window.open(
                  `${apiUrl}/auth/customer/set-auth?token=${token}&redirectTo=${redirectTo}`,
                  '_top'
                )

              openSnackbar({
                severity: 'success',
                title: `You’ve entered your account `,
              })
            } else {
              setIncorrectCode(true)
            }
          })
        )
  }

  return (
    <>
      <Typography variant="body1" mb="44px">
        The code was sent to <span style={{ fontWeight: 600 }}>{email}</span> <br />
        Please enter the code
      </Typography>
      <Stack direction="row" justifyContent="center" sx={{ position: 'relative' }}>
        {incorrectCode && (
          <Typography
            variant="body2"
            sx={theme => ({
              position: 'absolute',
              width: '100%',
              top: '-24px',
              left: 0,
              color: theme.palette.error.main,
            })}
          >
            Incorrect Code
          </Typography>
        )}
        {otpCodes.map((code, index) => (
          <Box
            key={index}
            sx={{
              width: '32px',
              height: '44px',
              margin: '0 4px',
              '&:nth-of-type(2)': { margin: '0 16px 0 4px' },
            }}
          >
            <TextField
              key={index}
              value={code}
              inputRef={(el: HTMLInputElement | null) => {
                inputRefs.current[index] = el
              }}
              className={incorrectCode ? 'error otpInput' : 'otpInput'}
              onChange={e => handleCodeChange(index, e.target.value)}
              onPaste={handlePaste}
              placeholder="&mdash;"
              size="small"
              inputProps={{
                type: 'number',
                pattern: '[0-9]*',
              }}
            />
          </Box>
        ))}
      </Stack>
      {!loading && (
        <>
          <Typography onClick={receiveOtpCode} mt="24px" variant="subtitle1">
            Send a Code Again
          </Typography>
          {/*
            VRG-720: this said "OTP code is valid for 15 minutes" — a WRONG
            number, shipped. `/auth/customer/login` stores the code through
            `OtpRepository`, whose redis implementation sets
            `ttl: OTP_TTL_SECONDS` = 600, so the real window is TEN minutes. The
            copy promised five minutes nobody had, which is worse than saying
            nothing: someone who believed it would come back at minute twelve
            to a code they were told was still good.

            A live countdown cannot drift from the constant the way a sentence
            can, which is the point of replacing rather than correcting it.
          */}
          <Box sx={{ m: '24px 0' }}>
            <CodeExpiryNotice
              issuedAt={codeIssuedAt}
              ttlSeconds={OTP_CODE_TTL_SECONDS}
              resendLabel='Use Send a Code Again above'
            />
          </Box>
        </>
      )}
      {loading && (
        <Box m="77px auto" sx={{ textAlign: 'center', position: 'relative', width: '84px', height: '84px' }}>
          <CircularProgress
            sx={theme => ({
              color: theme.palette.primary.contrastText,
              position: 'absolute',
              transform: 'translate(-50%, -50%)',
              left: 0,
            })}
            thickness={1}
            size={84}
          />
          <Box
            sx={theme => ({
              width: '84px',
              height: '84px',
              borderRadius: '84px',
              border: `1px solid ${theme.palette.grey[300]}`,
            })}
          />
        </Box>
      )}
    </>
  )
}
